import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { BehaviorSubject, Observable, Subject, firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { jwtDecode } from 'jwt-decode';

import { SERVER_API_URL } from '../../app.constants';
import { Account } from '../user/account.model';
import { UserService } from '../user/user.service';
import { AppConfigService } from '../config/app-config.service';
import { CandidateSessionService } from './candidate-session.service';
import { LANGUAGES } from '../language/language.constants';

@Injectable({ providedIn: 'root' })
export class AccountService {
  userIdentity: Account | null = null;
  loadLanguages: string[] = LANGUAGES;

  private authenticated = false;
  private authenticationState = new BehaviorSubject<Account | null>(null);
  private tooltipStatus = new Subject<boolean>();
  private identityPromise: Promise<Account | null> | null = null;

  private http = inject(HttpClient);
  private sanitizer = inject(DomSanitizer);
  private translateService = inject(TranslateService);
  private userService = inject(UserService);
  private appConfig = inject(AppConfigService);
  private candidateSession = inject(CandidateSessionService);

  isAuthenticated(): boolean {
    return this.authenticated;
  }

  hasAnyFeature(features: string[]): boolean {
    if (!this.authenticated || !this.userIdentity || !this.userIdentity.features || !this.userIdentity.rootNodes?.length) {
      return false;
    }
    const userFeatureKeys = this.userIdentity.features.map(f => f.key);
    return features.some(feature => userFeatureKeys.includes(feature));
  }

  hasFeature(feature: string): Promise<boolean> {
    if (!this.authenticated) {
      return Promise.resolve(false);
    }
    return this.identity().then(
      account => {
        if (!account?.features) return false;
        return account.features.map(f => f.key).includes(feature);
      },
      () => false
    );
  }

  hasAnyAuthority(authorities: string[]): boolean {
    if (!this.authenticated || !this.userIdentity || !this.userIdentity.authorities) {
      return false;
    }
    return authorities.some(auth => this.userIdentity!.authorities.includes(auth));
  }

  getAuthenticationState(): Observable<Account | null> {
    return this.authenticationState.asObservable();
  }

  authenticate(identity: Account | null): void {
    this.userIdentity = identity;
    this.authenticated = identity !== null;
    this.authenticationState.next(this.userIdentity);
  }

  /**
   * Clear all in-memory auth state on logout. Without this, the route guard keeps
   * trusting the cached `userIdentity`/`identityPromise` on browser-back (SPA navigation),
   * resurrecting a token-less session that then jitters / loops. (LB / KCS-565 logout fix.)
   */
  signOut(): void {
    this.userIdentity = null;
    this.authenticated = false;
    this.identityPromise = null;
    // Drop the cached language so a different/next user on this browser doesn't inherit it before
    // their own profile language loads (fixes different-user-same-tab and incognito re-login).
    localStorage.removeItem('userLanguage');
    this.authenticationState.next(null);
  }

  identity(force?: boolean): Promise<Account | null> {
    if (force) {
      // Drop only the cached promise; KEEP the current identity until the fresh account arrives.
      // Nulling userIdentity synchronously here mutated auth state mid-change-detection (dialogs
      // calling identity(true) from ngOnInit), flipping the main layout's isAllowed()/@if branches
      // and throwing NG0100 — which aborts the render pass and silently swallows UI updates
      // (toasts, row re-renders) in the same tick.
      this.identityPromise = null;
    }

    if (!force && this.userIdentity) {
      return Promise.resolve(this.userIdentity);
    }

    if (this.identityPromise) {
      return this.identityPromise;
    }

    // KCS-565 Part 2: a candidate session on MSL has no Azure token, so it resolves identity via the
    // /api/account endpoint (the LDAP path) using its own Bearer — never the MSAL token sync.
    this.identityPromise = (this.appConfig.microsoftLogin && !this.candidateSession.isActive()
      ? this.syncUserData()
      : this.fetchAccount()
    ).then(account => {
      if (account) {
        this.userIdentity = account;
        this.authenticated = true;

        // The logged-in user's profile language is authoritative. Apply it unconditionally so it
        // OVERRIDES any stale `userLanguage` left by a previous user or a different tab — this is
        // what fixes the flag/content mismatch on incognito, re-login, and different-user-same-browser.
        // The backend always returns a concrete langKey (defaults to 'de'); we coalesce again defensively.
        const langKey = this.userIdentity.langKey || 'de';
        this.translateService.use(langKey);
        localStorage.setItem('userLanguage', langKey);
      } else {
        this.userIdentity = null;
        this.authenticated = false;
      }
      this.authenticationState.next(this.userIdentity);
      this.identityPromise = null;
      return this.userIdentity;
    }).catch(err => {
      this.userIdentity = null;
      this.authenticated = false;
      this.authenticationState.next(this.userIdentity);
      this.identityPromise = null;
      return null;
    });

    return this.identityPromise;
  }

  getProfileImage(): Promise<SafeResourceUrl | string | null> {
    // No Microsoft Graph photo for non-MSAL sessions (LDAP staff or KCS-565 candidates have no Azure token).
    if (!this.appConfig.microsoftLogin || this.candidateSession.isActive()) {
      return Promise.resolve(null);
    }

    return firstValueFrom(
      this.http.get('https://graph.microsoft.com/v1.0/me/photos/48x48/$value', {
        headers: { 'Content-Type': 'image/*' },
        responseType: 'arraybuffer',
      })
    ).then(
      (data) => {
        const typedArray = new Uint8Array(data);
        const stringChar = String.fromCharCode(...typedArray);
        const base64String = btoa(stringChar);
        return this.sanitizer.bypassSecurityTrustUrl('data:image/*;base64, ' + base64String);
      },
      () => null
    );
  }

  checkForDevice(userId: any, deviceId: string): Observable<HttpResponse<any>> {
    return this.http.get<any>(SERVER_API_URL + `api/account/device/${userId}/${deviceId}`, { observe: 'response' });
  }

  enableOrDisableTooltip(checked: boolean): Observable<HttpResponse<any>> {
    return this.http.post<any>(SERVER_API_URL + 'api/account/tooltip', checked, {
      observe: 'response',
      headers: { 'Content-Type': 'application/json' },
    });
  }

  sendToolTipStatus(checked: boolean): void {
    this.tooltipStatus.next(checked);
  }

  getToolTipStatus(): Observable<boolean> {
    return this.tooltipStatus.asObservable();
  }

  changeLanguage(lang: string): Observable<HttpResponse<any>> {
    return this.http.post<any>(SERVER_API_URL + 'api/account/change-lang', lang, { observe: 'response' });
  }

  isUserGlobalLogin(): boolean {
    return this.userIdentity?.globalLogin ?? false;
  }

  getUserCurrentEntryNode(): string | null {
    return this.userIdentity?.rootNodes?.[0] ?? null;
  }

  // --- Private helpers ---

  private fetch(): Observable<HttpResponse<Account>> {
    return this.http.get<Account>(SERVER_API_URL + 'api/account', { observe: 'response' });
  }

  private fetchAccount(): Promise<Account | null> {
    return firstValueFrom(this.fetch()).then(
      response => response.body,
      () => null
    );
  }

  private syncUserData(): Promise<Account | null> {
    return firstValueFrom(this.userService.token()).then(token => {
      const decoded: any = jwtDecode(token.tokenValue);
      return firstValueFrom(
        this.userService.saveUser(
          decoded.oid ?? decoded.id,
          decoded.name,
          decoded.groups,
          decoded.preferred_username,
          decoded.Department,
          decoded.EmployeeId
        )
      ).then(response => response.body);
    }).catch(err => {
      console.error('Failed to sync user data:', err);
      return null;
    });
  }
}
